import { Component, ViewChild, type OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SharedService } from '@shared/services/shared.service';
import { IIndividualLicense } from 'app/models/licenses/individual-license';
import { IndividualLicenseService } from 'app/services/license/individual-license.service';
import { IndividualApplicantComponent } from '../individual-applicant/individual-applicant.component';
import { IStaff } from 'app/models/user/staff';
import { ISearch } from 'app/models/licenses/search';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatSelectChange } from '@angular/material/select';
import { trigger, transition, style, animate } from '@angular/animations';

@Component({
  selector: 'app-individual-license',
  templateUrl: './individual-license.component.html',
  styleUrls: ['./individual-license.component.css'],
  animations: [
    trigger('slideLeft', [
      transition(':enter', [
        style({ transform: 'translateX(-100%)' }),
        animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(-100%)' }))
      ])
    ]),
    trigger('slideRight', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(100%)' }))
      ])
    ])
  ]
})

export class IndividualLicenseComponent implements OnInit {
  @ViewChild(IndividualApplicantComponent) individualApplicants: IndividualApplicantComponent;

  public search: ISearch = {
    entityName: '',
    firstName: '',
    lastName: '',
    licenseId: '',
    statuses: [],
    assignedTo: ''
  }
  public searchForm = new FormGroup({
    firstName: new FormControl(''),
    lastName: new FormControl(''),
    licenseId: new FormControl(''),
    assignedTo: new FormControl(''),
    statuses: new FormControl<string[] | null>(['Submitted'])
  });

  public showCreateLicense: boolean = false;
  public step: number = 0;
  public licenseStaff: IStaff[] = [];
  public licenses: IIndividualLicense[] = [];
  public license: IIndividualLicense = {
    id: 0,
    userId: '',
    licenseId: '',
    enteredByStaff: false,
    assignedToId: '',
    assignedToName: '',
    finalApprovalBy: '',
    canRenew: false,
    isRenewal: false,
    electronicSignature: '',
    applicationDate: '',
    approvedDate: '',
    deniedDate: '',
    expirationDate: '',
    statuses: []
  }
  public licenseForm = new FormGroup({
    electronicSignature: new FormControl("", Validators.required)
  });

  public licenseColumns: string [] = ['licenseId', 'legalFirstName', 'legalLastName', 'statuses', 'assign'];
  public licenseSource = new MatTableDataSource<IIndividualLicense>(this.licenses);
  @ViewChild('licensePaginator', {static: false}) licensePaginator: MatPaginator;
  @ViewChild('licenseSort', {static: false}) licenseSort: MatSort;

  public sharedService = inject(SharedService);
  public licenseService = inject(IndividualLicenseService);

  ngOnInit(): void {
    this.getStaff();
    this.searchLicenses();
  }

  searchLicenses(): void {
    let form = this.searchForm.value;
    this.search.firstName = form.firstName;
    this.search.lastName = form.lastName;
    this.search.licenseId = form.licenseId;
    this.search.statuses = form.statuses;
    if(this.search.statuses[0] === null){
      this.search.statuses = [];
    }
    this.search.assignedTo = form.assignedTo;

    this.licenseService.searchLicenses(this.search).subscribe({
      next: response => this.licenses = response,
      error: e => {
        this.sharedService.toastr.error("Could not retrieve licenses");
        console.log('error searching licenses', e);
      },
      complete: () => this.updateLicenseTable()
    });
  }

  getStaff(): void{
    this.licenseService.getStaff().subscribe({
      next: response => this.licenseStaff = response,
      error: e => console.log('error retrieving staff', e)
    });
  }

  updateLicenseTable(): void {
    setTimeout(() => {
      this.licenseSource.data = this.licenses;
      this.licenseSource.sort = this.licenseSort;
      this.licenseSource.paginator = this.licensePaginator;
    }, 500);
  }

  filterTable(event: Event): void {
    let value = (event.target as HTMLInputElement).value;
    let filter = value.trim().toLocaleLowerCase();
    this.licenseSource.filter = filter;
  }

  assignLicense(event: MatSelectChange, licenseId: number): void {
    let user: IStaff = this.licenseStaff.find(staff => staff.name === event.value);
    if(user === undefined){
      let emptyUser: IStaff = {
        id: '',
        name: '',
        email: ''
      }
      user = emptyUser;
    }
    this.licenseService.assignLicense(licenseId, user).subscribe(
      () =>{
        this.licenses.find(tp => tp.id == licenseId).assignedToName = user.name;
        if(user === null || user.name === null || user.name.length === 0) {
          this.sharedService.toastr.success("License Unassigned");
        }
        else {
          this.sharedService.toastr.success("License assigned to " + user.name);
        }
      },
      error => console.log('error', error)
    );
  }

  createLicense(): void {
    this.sharedService.openConfirm("Start an application for an individual license?");
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          this.licenseService.createLicense().subscribe({
            next: response => this.license = response,
            error: e => {
              console.log("Error Creating License", e);
              this.sharedService.toastr.error("Error Creating License: " + e);
            },
            complete: () => {
              this.licenses.push(this.license);
              this.showCreateLicense = false;
              this.sharedService.toastr.success("Created Application!");
            }
          });
        }
      }
    );
  }

  selectLicense(license: IIndividualLicense): void {
    this.license = license;
    this.licenseForm.reset({
      electronicSignature: license.electronicSignature
    });
  }

  closeLicense(): void {
    let emptyLicense: IIndividualLicense = {
      id: 0,
      userId: '',
      licenseId: '',
      enteredByStaff: false,
      assignedToId: '',
      assignedToName: '',
      finalApprovalBy: '',
      canRenew: false,
      isRenewal: false,
      electronicSignature: '',
      applicationDate: '',
      approvedDate: '',
      deniedDate: '',
      expirationDate: '',
      statuses: []
    }

    this.license = emptyLicense;
    this.licenseForm.reset({
      electronicSignature: ''
    });
  }

  submitLicense(approved: boolean): void {
    this.sharedService.openConfirm(approved? 'Approve Application?' : 'Deny Application?');
    this.sharedService.confirmed().subscribe(
      (response) => {
        if(response){
          this.licenseService.approveLicense(this.license.id, approved).subscribe({
            next: response => {
              let licenseIndex: number = this.licenses.findIndex(l => l.id === this.license.id);
              this.licenses[licenseIndex] = response;
              this.license = response;
            },
            error: e => {
              console.log('error', e);
              this.sharedService.toastr.error("Could Not Complete License");
            },
            complete: () => {
              this.sharedService.toastr.success("License Completed");
              this.closeLicense();
            }
          });
        }
      }
    );
  }

  compareFn(type1: string, type2: string): boolean {
    return type1 === type2;
  }

  public get locked(): boolean {
    return this.license.statuses.some(s => s.label === this.sharedService.submitted);
  }

  public get complete(): boolean {
    return this.license.statuses.some(s => s.label === this.sharedService.approved || s.label === this.sharedService.denied);
  }

  public get applicationValid(): boolean {
    let applicantValid: boolean = this.individualApplicants.applicantForm.valid;
    let documentsValid: boolean = this.individualApplicants.documents?.documents.length > 0;
    let addressesValid: boolean = this.individualApplicants.addresses?.addresses.length > 0;
    return applicantValid && documentsValid && addressesValid;
  }

}
